import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Copy, Mail, School, Send } from "lucide-react";
import { toast } from "sonner";
import { ComposeSchoolEmail, EmailHistory } from "./ComposeSchoolEmail";

function SchoolCard({ g, onlyPass, onCompose }) {
  const [open, setOpen] = useState(false);
  const fams = onlyPass ? g.families.filter((f) => f.unused_welcome_pass) : g.families;
  if (!fams.length) return null;
  const emails = fams.map((f) => f.email).join(", ");
  const copy = async () => {
    try { await navigator.clipboard.writeText(emails); toast.success(`${fams.length} emails copied — paste into BCC`); }
    catch { window.prompt("Copy these emails:", emails); }
  };
  const slug = g.school.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return (
    <div className="border border-slate-200 rounded-2xl p-3" data-testid={`school-list-${slug}`}>
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setOpen(!open)} className="font-bold text-navy text-left flex-1 min-w-0" data-testid={`school-toggle-${slug}`}>{g.school} <span className="text-xs text-slate-400 font-normal">· {fams.length} famil{fams.length === 1 ? "y" : "ies"}</span></button>
        <Button size="sm" className="rounded-full bg-navy text-white font-bold" onClick={() => onCompose(g.school, fams.length)} data-testid={`school-compose-${slug}`}><Send className="w-3.5 h-3.5 mr-1" /> Write email</Button>
        <Button size="sm" variant="outline" className="rounded-full" onClick={copy} data-testid={`school-copy-${slug}`}><Copy className="w-3.5 h-3.5 mr-1" /> Copy emails</Button>
        <a href={`mailto:?bcc=${encodeURIComponent(fams.map((f) => f.email).join(","))}&subject=${encodeURIComponent("Adventure Rock Bermuda")}`} data-testid={`school-mailto-${slug}`}>
          <Button size="sm" variant="outline" className="rounded-full"><Mail className="w-3.5 h-3.5 mr-1" /> Open in my email app</Button></a>
      </div>
      {open && <div className="mt-2 divide-y divide-slate-100 text-sm">{fams.map((f) => (
        <p key={f.email} className="py-1.5 flex flex-wrap justify-between gap-2"><span><b className="text-navy">{f.parent_name}</b> <span className="text-slate-400">· {f.explorers.join(", ")}</span></span>
          <span className="text-slate-500">{f.email}{f.phone ? ` · ${f.phone}` : ""}{f.unused_welcome_pass && <span className="ml-1 text-[10px] bg-gold/30 text-navy rounded-full px-2">pass unused</span>}</span></p>))}</div>}
    </div>
  );
}

export const SchoolEmailLists = () => {
  const [groups, setGroups] = useState([]);
  const [onlyPass, setOnlyPass] = useState(false);
  const [compose, setCompose] = useState(null);
  const [sentTick, setSentTick] = useState(0);
  useEffect(() => { api.get("/admin/school-email-lists").then((r) => setGroups(r.data)).catch((e) => toast.error(apiErr(e))); }, []);
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3" data-testid="school-email-lists">
      <div className="flex flex-wrap items-center gap-2">
        <School className="w-5 h-5 text-turquoise" />
        <p className="font-display font-bold text-navy">Parent emails by school</p>
        <label className="ml-auto flex items-center gap-2 text-xs text-slate-600"><input type="checkbox" checked={onlyPass} onChange={(e) => setOnlyPass(e.target.checked)} data-testid="school-only-unused-pass" /> Only families with an unused Welcome Pass</label>
      </div>
      <p className="text-xs text-slate-500">Grouped by the school on each Explorer's profile. "Write email" sends each parent their own private email from Adventure Rock — no email app needed.</p>
      <div className="space-y-2">{groups.map((g) => <SchoolCard key={g.school} g={g} onlyPass={onlyPass} onCompose={(school, count) => setCompose({ school, count })} />)}</div>
      {!groups.length && <p className="text-sm text-slate-400">No parent emails yet.</p>}
      <EmailHistory refresh={sentTick} />
      <ComposeSchoolEmail school={compose?.school} count={compose?.count || 0} onlyPass={onlyPass} onClose={(sent) => { setCompose(null); if (sent) setSentTick((t) => t + 1); }} />
    </div>
  );
};
